/**
 * IntentionFilters Component
 * Filter bar for intention detail page — used across all tabs.
 * Individual filter pills are shown/hidden via props.
 */

import { X } from "lucide-react"
import { FilterChipButton } from "@/components/shared/FilterChipButton"
import { PriceFilterPopover } from "@/components/shared/PriceFilterPopover"
import { RadioPopover } from "@/components/shared/RadioPopover"
import { SingleToggleChipPopover } from "@/components/shared/SingleToggleChipPopover"
import { ToggleChipPopover } from "@/components/shared/ToggleChipPopover"
import { useTranslation } from "@/language"
import type { IntentionFiltersState } from "../types"

interface IntentionFiltersProps {
  filters: IntentionFiltersState
  intentionName: string
  onChange: (filters: IntentionFiltersState) => void
  traditionsList: Array<{ id: string; title: string }>
  showPrice?: boolean
  showFormat?: boolean
  showContent?: boolean
  showIntention?: boolean
  showSpiritualTraditions?: boolean
  showDuration?: boolean
}

export function IntentionFilters({
  filters,
  intentionName,
  onChange,
  traditionsList,
  showPrice = false,
  showFormat = false,
  showContent = false,
  showIntention = false,
  showSpiritualTraditions = false,
  showDuration = false,
}: IntentionFiltersProps) {
  const { t } = useTranslation("learnPractice")

  const sortOptions = [
    { value: "trending", label: t("filters.trending") },
    { value: "newest", label: t("filters.newest") },
  ]

  const formatOptions = [
    { value: "all", label: t("detail.filters.allFormats") },
    { value: "IN_PERSON", label: t("detail.filters.inPerson") },
    { value: "VIRTUAL_COURSE", label: t("detail.filters.liveVirtual") },
    { value: "SELF_GUIDED", label: t("detail.filters.selfGuided") },
    { value: "SELF_GUIDED_COURSE", label: t("detail.filters.selfGuidedCourse") },
  ]

  const durationOptions = [
    { value: "all", label: t("detail.filters.allDurations") },
    { value: "short", label: t("filters.short") },
    { value: "medium", label: t("filters.medium") },
    { value: "long", label: t("filters.long") },
  ]

  const hasActiveFilters =
    filters.price !== "all" ||
    filters.format !== "all" ||
    filters.duration !== "all" ||
    (filters.traditions !== undefined && filters.traditions !== "")

  const resetFilters = () => {
    onChange({
      ...filters,
      sort: "trending",
      price: "all",
      format: "all",
      duration: "all",
      traditions: undefined,
      content: undefined,
    })
  }

  return (
    <div className="py-0 pt-6">
      <div className="flex items-center gap-[10px] flex-nowrap overflow-x-auto -mx-5 px-5 md:mx-0 md:px-0">
        {/* Sort — always shown */}
        <RadioPopover
          label={t("filters.sortBy")}
          options={sortOptions}
          value={filters.sort}
          onChange={v => onChange({ ...filters, sort: v as IntentionFiltersState["sort"] })}
        />

        {/* Price */}
        {showPrice && (
          <PriceFilterPopover
            selectedPrice={filters.price !== "all" ? [filters.price] : []}
            priceRange={[0, 100]}
            onApply={price => {
              const filtered = price.filter(v => v !== "all")
              onChange({
                ...filters,
                price: (filtered.length > 0
                  ? filtered[0]
                  : "all") as IntentionFiltersState["price"],
              })
            }}
          />
        )}

        {/* Format */}
        {showFormat && (
          <SingleToggleChipPopover
            label={t("filters.format")}
            options={formatOptions}
            selected={filters.format}
            allValue="all"
            onApply={v =>
              onChange({
                ...filters,
                format: (v ?? "all") as IntentionFiltersState["format"],
              })
            }
          />
        )}

        {/* Content */}
        {showContent && (
          <ToggleChipPopover
            label={t("filters.content")}
            options={[
              { value: "videos", label: t("detail.filters.video") },
              { value: "audio", label: t("detail.filters.audio") },
              { value: "no-media", label: t("detail.filters.noMedia") },
            ]}
            selected={
              filters.content
                ? (filters.content.split(",") as Array<"videos" | "audio" | "no-media">)
                : []
            }
            onApply={vals => {
              onChange({
                ...filters,
                content:
                  vals.length > 0
                    ? (vals.join(",") as IntentionFiltersState["content"])
                    : undefined,
              })
            }}
          />
        )}

        {/* Intention (read-only) */}
        {showIntention && (
          <FilterChipButton label={t("detail.filters.intentionPrefix")} value={intentionName} />
        )}

        {/* Spiritual Traditions */}
        {showSpiritualTraditions && (
          <ToggleChipPopover
            label={t("filters.spiritualTraditions")}
            options={traditionsList.map(t => ({ value: t.id, label: t.title }))}
            selected={filters.traditions ? filters.traditions.split(",") : []}
            onApply={vals => {
              onChange({
                ...filters,
                traditions: vals.length > 0 ? vals.join(",") : undefined,
              })
            }}
            maxWidth="480px"
          />
        )}

        {/* Duration */}
        {showDuration && (
          <SingleToggleChipPopover
            label={t("filters.duration")}
            options={durationOptions}
            selected={filters.duration}
            allValue="all"
            onApply={v =>
              onChange({
                ...filters,
                duration: (v ?? "all") as IntentionFiltersState["duration"],
              })
            }
          />
        )}

        {/* Reset All */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center gap-[5px] pl-[10px] text-[14px] font-semibold text-brand-terracotta hover:text-brand-terracotta/80 transition-colors shrink-0"
          >
            <span className="size-[20px] rounded-full border border-brand-terracotta flex items-center justify-center">
              <X className="size-[12px]" />
            </span>
            {t("filters.reset")}
          </button>
        )}
      </div>
    </div>
  )
}
